import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CalendarIcon, Plus, Trash2, ArrowRight, ArrowLeft, Loader2, ChevronDown, ChevronRight, Pencil, AlertTriangle, Copy, ChevronsUpDown, ArrowUp, ArrowDown, FileText } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { CustomerSelectWithCreate } from "@/components/controller/CustomerSelectWithCreate";
import {
  useCreateEstimate,
  useUpdateEstimate,
  LineItem,
  SubLine,
  recalcLineItem,
  JobType,
  Estimate,
  isNewFormat,
} from "@/hooks/useEstimates";
import { useEstimateTemplates, EstimateTemplate, TemplateLineItem } from "@/hooks/useEstimateTemplates";
import { useProductsServices } from "@/hooks/useProductsServices";
import { toast } from "sonner";

const UNITS = ["SF", "LF", "EA", "LS", "HR", "DAY", "TON", "GAL", "BAG", "ROLL", "SQ", "BOX", "BDL"];

// Product codes prioritized by job type
const JOB_TYPE_PRIORITY: Record<string, string[]> = {
  flooring: ['LAB-001','LAB-002','LAB-003','LAB-004','LAB-005','LAB-006','LAB-007','MAT-001','MAT-002','MAT-003','MAT-004','MAT-005','MAT-006','MAT-007','MAT-008','MAT-009','MAT-010','MAT-011','MAT-012','MAT-013','MAT-014','MAT-015'],
  roofing: ['LAB-009','LAB-014','MAT-016','MAT-017','MAT-018','MAT-019'],
  emergency: ['LAB-008','EQP-004','EQP-005','EQP-006'],
};

interface SubLineDraft {
  id: string;
  type: "labor" | "material";
  description: string;
  qty: string;
  unit: string;
  cost_price: string;
  markup_pct: string;
  unit_price: string;
  taxable: boolean;
  product_service_id?: string;
  income_account?: string;
  cogs_account?: string;
}

interface LineDraft {
  description: string;
  customer_description: string;
  qty: string;
  unit: string;
  sub_lines: SubLineDraft[];
  expanded: boolean;
}

const calcUnitPrice = (cost: string, markup: string) => {
  const c = parseFloat(cost) || 0;
  const m = parseFloat(markup) || 0;
  return c * (1 + m / 100);
};

const draftSubLine = (type: "labor" | "material", parentQty?: string, parentUnit?: string, parentDesc?: string): SubLineDraft => ({
  id: crypto.randomUUID(),
  type,
  description: parentDesc ? (type === "labor" ? `${parentDesc} - Labor` : parentDesc) : "",
  qty: parentQty ?? "1",
  unit: parentUnit ?? "SF",
  cost_price: "0",
  markup_pct: type === "labor" ? "35" : "20",
  unit_price: "0",
  taxable: type === "material",
});

const emptyLine = (): LineDraft => ({
  description: "",
  customer_description: "",
  qty: "1",
  unit: "SF",
  sub_lines: [draftSubLine("labor")],
  expanded: true,
});

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const subLineBilled = (sl: SubLineDraft) => (parseFloat(sl.qty) || 0) * (parseFloat(sl.unit_price) || 0);
const subLineCost = (sl: SubLineDraft) => (parseFloat(sl.qty) || 0) * (parseFloat(sl.cost_price) || 0);

const calcLineTotal = (l: LineDraft) => l.sub_lines.reduce((s, sl) => s + subLineBilled(sl), 0);

function templateToLines(tpl: EstimateTemplate): LineDraft[] {
  return (tpl.line_items as TemplateLineItem[]).map((tli) => {
    const sub_lines: SubLineDraft[] = tli.sub_line_types.map((t) =>
      draftSubLine(t, "1", tli.unit, tli.description)
    );
    // Ensure cost fields are 0
    sub_lines.forEach(sl => { sl.cost_price = "0"; sl.unit_price = "0"; });
    return {
      description: tli.description,
      customer_description: "",
      qty: "1",
      unit: tli.unit,
      sub_lines,
      expanded: true,
    };
  });
}

/** Convert draft lines to the final LineItem format */
function draftsToLineItems(lines: LineDraft[]): LineItem[] {
  return lines
    .filter((l) => l.description.trim() || l.sub_lines.some((sl) => sl.description.trim()))
    .map((l, idx) => {
      const subLines: SubLine[] = l.sub_lines
        .filter((sl) => sl.description.trim())
        .map((sl) => ({
          id: sl.id,
          type: sl.type,
          description: sl.description,
          qty: parseFloat(sl.qty) || 0,
          unit: sl.unit,
          cost_price: parseFloat(sl.cost_price) || 0,
          markup_pct: parseFloat(sl.markup_pct) || 0,
          unit_price: parseFloat(sl.unit_price) || 0,
          total_cost: subLineCost(sl),
          total_billed: subLineBilled(sl),
          total: subLineBilled(sl),
          taxable: sl.taxable,
          qb_tax_code: sl.taxable ? ("TAX" as const) : ("NON" as const),
          qb_item_ref: sl.type === "labor" ? "Installation Labor" : "Job Materials",
          product_service_id: sl.product_service_id || undefined,
          income_account: sl.income_account || undefined,
          cogs_account: sl.cogs_account || undefined,
        }));

      const laborCost = subLines.filter((s) => s.type === "labor").reduce((a, s) => a + s.total_cost, 0);
      const laborBilled = subLines.filter((s) => s.type === "labor").reduce((a, s) => a + s.total_billed, 0);
      const materialCost = subLines.filter((s) => s.type === "material").reduce((a, s) => a + s.total_cost, 0);
      const materialBilled = subLines.filter((s) => s.type === "material").reduce((a, s) => a + s.total_billed, 0);

      return recalcLineItem({
        line_num: idx + 1,
        description: l.description,
        customer_description: l.customer_description || l.description,
        qty: parseFloat(l.qty) || 0,
        unit: l.unit,
        sub_lines: subLines,
        labor_total_cost: laborCost,
        labor_total_billed: laborBilled,
        material_total_cost: materialCost,
        material_total_billed: materialBilled,
        total_cost: laborCost + materialCost,
        total_billed: laborBilled + materialBilled,
      });
    });
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editEstimate?: Estimate | null;
  defaultCustomerId?: string;
}

/** Convert an existing Estimate's line items back into draft format for editing */
function estimateToLineDrafts(estimate: Estimate): LineDraft[] {
  const items = estimate.line_items ?? [];
  if (!items.length) return [emptyLine()];
  
  return items.map((item: any) => {
    if (isNewFormat(item) || Array.isArray(item.sub_lines)) {
      return {
        description: item.description || "",
        customer_description: item.customer_description || "",
        qty: String(item.qty ?? 1),
        unit: item.unit || "SF",
        sub_lines: (item.sub_lines || []).map((sl: any): SubLineDraft => ({
          id: sl.id || crypto.randomUUID(),
          type: sl.type || "labor",
          description: sl.description || "",
          qty: String(sl.qty ?? 1),
          unit: sl.unit || "SF",
          cost_price: String(sl.cost_price ?? 0),
          markup_pct: String(sl.markup_pct ?? 0),
          unit_price: String(sl.unit_price ?? 0),
          taxable: sl.taxable ?? (sl.type === "material"),
          product_service_id: sl.product_service_id || undefined,
          income_account: sl.income_account || undefined,
          cogs_account: sl.cogs_account || undefined,
        })),
        expanded: true,
      } as LineDraft;
    }
    // Legacy flat format
    return {
      description: item.description || "",
      customer_description: "",
      qty: String(item.qty ?? 1),
      unit: item.unit || "SF",
      sub_lines: [draftSubLine("labor")],
      expanded: true,
    } as LineDraft;
  });
}

export const NewEstimateDialog = ({ open, onOpenChange, editEstimate, defaultCustomerId }: Props) => {
  
  const { data: templates } = useEstimateTemplates();
  const { data: allProducts } = useProductsServices();
  const createEstimate = useCreateEstimate();
  const updateEstimate = useUpdateEstimate();
  const isEditMode = !!editEstimate;
  const [step, setStep] = useState(1);
  const [initialized, setInitialized] = useState(false);

  // Step 1
  const [customerId, setCustomerId] = useState("");
  const [jobType, setJobType] = useState<JobType>("general");
  const [txnDate, setTxnDate] = useState<Date>(new Date());
  const [expiryDate, setExpiryDate] = useState<Date | undefined>();
  const [notes, setNotes] = useState("");

  // Step 2
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [viewMode, setViewMode] = useState<"internal" | "customer">("internal");
  const [laborMarkup, setLaborMarkup] = useState("35");
  const [materialMarkup, setMaterialMarkup] = useState("20");
  const [allExpanded, setAllExpanded] = useState(true);

  // Template loading
  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<EstimateTemplate | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // Pre-fill from editEstimate when opening in edit mode
  if (open && editEstimate && !initialized) {
    setCustomerId(editEstimate.customer_id || "");
    setJobType(editEstimate.job_type || "general");
    setTxnDate(new Date(editEstimate.txn_date));
    setExpiryDate(editEstimate.expiry_date ? new Date(editEstimate.expiry_date) : undefined);
    setNotes(editEstimate.notes || "");
    setLines(estimateToLineDrafts(editEstimate));
    setInitialized(true);
  }
  // Pre-fill customer from defaultCustomerId (new estimate from customer detail)
  if (open && !editEstimate && !initialized && defaultCustomerId) {
    setCustomerId(defaultCustomerId);
    setInitialized(true);
  }
  if (!open && initialized) {
    setInitialized(false);
  }

  // ============================================
  // SMART QUANTITY MAPPING — Live Reactive Engine
  // ============================================
  const overriddenMappings = useRef<Set<string>>(new Set());

  const applySmartMappings = useCallback((currentLines: LineDraft[]): LineDraft[] => {
    if (!allProducts?.length) return currentLines;

    // Build a map of product_code → qty across all sub-lines
    const codeQtyMap = new Map<string, number>();
    currentLines.forEach(l => {
      l.sub_lines.forEach(sl => {
        if (!sl.product_service_id) return;
        const product = allProducts.find(p => p.id === sl.product_service_id);
        if (product) {
          const existing = codeQtyMap.get(product.code) || 0;
          codeQtyMap.set(product.code, existing + (parseFloat(sl.qty) || 0));
        }
      });
    });

    let changed = false;
    const newLines = currentLines.map(l => ({
      ...l,
      sub_lines: l.sub_lines.map(sl => {
        if (!sl.product_service_id) return sl;
        // Skip if user has manually overridden
        if (overriddenMappings.current.has(sl.id)) return sl;

        const product = allProducts.find(p => p.id === sl.product_service_id);
        if (!product?.qty_mapping || !product.qty_mapping.auto_apply) return sl;

        const mapping = product.qty_mapping;
        let calculatedQty: number | null = null;
        let calculatedCost: number | null = null;

        if (mapping.type === 'ratio' && mapping.links?.length) {
          const link = mapping.links[0];
          const sourceQty = codeQtyMap.get(link.product_code) || 0;
          if (sourceQty === 0) return sl;

          let result = sourceQty;
          if (link.operation === 'divide') result = sourceQty / (link.value || 1);
          else if (link.operation === 'multiply') result = sourceQty * (link.value || 1);

          if (link.round === 'ceil') result = Math.ceil(result);
          else if (link.round === 'floor') result = Math.floor(result);
          else if (link.round === 'round') result = Math.round(result);

          calculatedQty = Math.max(1, result);
        } else if (mapping.type === 'sum_multiply' && mapping.links?.length) {
          let sum = 0;
          mapping.links.forEach((link: any) => {
            const linkQty = codeQtyMap.get(link.product_code) || 0;
            if (link.operation === 'add') sum += linkQty;
          });
          if (sum === 0) return sl;
          if (mapping.use_as === 'cost_price' && mapping.then_multiply) {
            calculatedCost = sum * mapping.then_multiply;
            calculatedQty = sum;
          } else {
            calculatedQty = sum;
          }
        }

        if (calculatedQty !== null && String(calculatedQty) !== sl.qty) {
          changed = true;
          const updatedSl = { ...sl, qty: String(calculatedQty) };
          if (calculatedCost !== null) {
            updatedSl.cost_price = String(calculatedCost / calculatedQty);
            updatedSl.unit_price = String(calcUnitPrice(updatedSl.cost_price, updatedSl.markup_pct));
          }
          return updatedSl;
        }
        if (calculatedCost !== null) {
          const costPerUnit = calculatedQty ? calculatedCost / calculatedQty : 0;
          if (String(costPerUnit) !== sl.cost_price) {
            changed = true;
            return {
              ...sl,
              cost_price: String(costPerUnit),
              unit_price: String(calcUnitPrice(String(costPerUnit), sl.markup_pct)),
            };
          }
        }
        return sl;
      }),
    }));

    return changed ? newLines : currentLines;
  }, [allProducts]);

  // Run smart mappings whenever lines change
  const prevLinesRef = useRef<string>("");
  useEffect(() => {
    const serialized = JSON.stringify(lines.map(l => l.sub_lines.map(sl => ({ id: sl.id, qty: sl.qty, pid: sl.product_service_id }))));
    if (serialized === prevLinesRef.current) return;
    prevLinesRef.current = serialized;

    const updated = applySmartMappings(lines);
    if (updated !== lines) {
      setLines(updated);
    }
  }, [lines, applySmartMappings]);

  const updateLine = (idx: number, field: keyof LineDraft, val: any) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: val } : l)));
  };

  const toggleExpand = (idx: number) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, expanded: !l.expanded } : l)));
  };

  const addSubLine = (lineIdx: number, type: "labor" | "material") => {
    setLines((prev) =>
      prev.map((l, i) =>
        i === lineIdx ? { ...l, sub_lines: [...l.sub_lines, draftSubLine(type, l.qty, l.unit, l.description)] } : l
      )
    );
  };

  const updateSubLine = (lineIdx: number, subIdx: number, field: keyof SubLineDraft, val: string | boolean) => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSubs = l.sub_lines.map((sl, si) => {
          if (si !== subIdx) return sl;
          const updated = { ...sl, [field]: val };
          // Track manual qty override for mapped products
          if (field === "qty" && sl.product_service_id) {
            const prod = allProducts?.find(p => p.id === sl.product_service_id);
            if (prod?.qty_mapping?.auto_apply) {
              overriddenMappings.current.add(sl.id);
            }
          }
          if (field === "cost_price" || field === "markup_pct") {
            const newCost = field === "cost_price" ? val as string : sl.cost_price;
            const newMarkup = field === "markup_pct" ? val as string : sl.markup_pct;
            updated.unit_price = String(calcUnitPrice(newCost, newMarkup));
          }
          if (field === "unit_price") {
            const cost = parseFloat(sl.cost_price) || 0;
            if (cost > 0) {
              const price = parseFloat(val as string) || 0;
              updated.markup_pct = String(Math.round(((price / cost) - 1) * 100 * 100) / 100);
            }
          }
          if (field === "unit" && val === "LS") {
            updated.qty = "1";
          }
          if (field === "taxable") {
            updated.taxable = val as boolean;
          }
          return updated;
        });
        return { ...l, sub_lines: newSubs };
      })
    );
  };

  const removeSubLine = (lineIdx: number, subIdx: number) => {
    setLines((prev) =>
      prev.map((l, i) =>
        i === lineIdx ? { ...l, sub_lines: l.sub_lines.filter((_, si) => si !== subIdx) } : l
      )
    );
  };

  const selectProduct = (lineIdx: number, subIdx: number, productId: string) => {
    const product = allProducts?.find((p) => p.id === productId);
    if (!product) return;
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== lineIdx) return l;
        const newSubs = l.sub_lines.map((sl, si) => {
          if (si !== subIdx) return sl;
          return {
            ...sl,
            description: product.name,
            unit: product.unit,
            markup_pct: String(product.default_markup_pct),
            taxable: product.is_taxable,
            product_service_id: product.id,
            income_account: product.income_account || undefined,
            cogs_account: product.cogs_account || undefined,
            unit_price: String(calcUnitPrice(sl.cost_price, String(product.default_markup_pct))),
          };
        });
        return { ...l, sub_lines: newSubs };
      })
    );
  };

  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const moveLine = (idx: number, dir: -1 | 1) => {
    setLines((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const duplicateLine = (idx: number) => {
    setLines((prev) => {
      const src = prev[idx];
      const dup: LineDraft = {
        description: src.description,
        customer_description: src.customer_description,
        qty: "1",
        unit: src.unit,
        sub_lines: src.sub_lines.map((sl) => ({
          ...sl,
          id: crypto.randomUUID(),
          qty: "1",
          cost_price: "0",
          markup_pct: sl.markup_pct,
          unit_price: "0",
        })),
        expanded: true,
      };
      const next = [...prev];
      next.splice(idx + 1, 0, dup);
      return next;
    });
  };

  const applyMasterMarkup = () => {
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        sub_lines: l.sub_lines.map((sl) => {
          const m = sl.type === "labor" ? laborMarkup : materialMarkup;
          return { ...sl, markup_pct: m, unit_price: String(calcUnitPrice(sl.cost_price, m)) };
        }),
      }))
    );
    toast.success(`Applied Labor ${laborMarkup}% / Material ${materialMarkup}% markup`);
  };

  const toggleAllExpanded = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    setLines((prev) => prev.map((l) => ({ ...l, expanded: next })));
  };

  const handleLoadTemplate = (tpl: EstimateTemplate) => {
    const hasContent = lines.some((l) => l.description.trim() || l.sub_lines.some((sl) => sl.description.trim()));
    if (hasContent) {
      setPendingTemplate(tpl);
    } else {
      setLines(templateToLines(tpl));
      setLaborMarkup(String(tpl.labor_markup_pct));
      setMaterialMarkup(String(tpl.material_markup_pct));
    }
    setTemplatePopoverOpen(false);
  };

  const applyTemplate = (mode: "replace" | "append") => {
    if (!pendingTemplate) return;
    const tplLines = templateToLines(pendingTemplate);
    if (mode === "replace") {
      setLines(tplLines);
    } else {
      setLines((prev) => [...prev, ...tplLines]);
    }
    setLaborMarkup(String(pendingTemplate.labor_markup_pct));
    setMaterialMarkup(String(pendingTemplate.material_markup_pct));
    setPendingTemplate(null);
  };

  const totals = useMemo(() => {
    let laborCost = 0, laborBilled = 0, materialCost = 0, materialBilled = 0;
    lines.forEach((l) => {
      l.sub_lines.forEach((sl) => {
        const c = subLineCost(sl);
        const b = subLineBilled(sl);
        if (sl.type === "labor") { laborCost += c; laborBilled += b; }
        else { materialCost += c; materialBilled += b; }
      });
    });
    const totalCost = laborCost + materialCost;
    const totalBilled = laborBilled + materialBilled;
    const profit = totalBilled - totalCost;
    const margin = totalBilled > 0 ? (profit / totalBilled) * 100 : 0;
    const laborMargin = laborBilled > 0 ? ((laborBilled - laborCost) / laborBilled) * 100 : 0;
    const materialMargin = materialBilled > 0 ? ((materialBilled - materialCost) / materialBilled) * 100 : 0;
    return { laborCost, laborBilled, laborMargin, materialCost, materialBilled, materialMargin, totalCost, totalBilled, profit, margin };
  }, [lines]);

  const reset = () => {
    setStep(1);
    setCustomerId("");
    setJobType("general");
    setTxnDate(new Date());
    setExpiryDate(undefined);
    setNotes("");
    setLines([emptyLine()]);
    setCustomerNotes("");
    setViewMode("internal");
    setLaborMarkup("35");
    setMaterialMarkup("20");
    setAllExpanded(true);
  };

  const handleSave = async (syncStatus: string) => {
    const lineItems = draftsToLineItems(lines);
    try {
      if (isEditMode && editEstimate) {
        await updateEstimate.mutateAsync({
          id: editEstimate.id,
          customer_id: customerId || null,
          txn_date: format(txnDate, "yyyy-MM-dd"),
          expiry_date: expiryDate ? format(expiryDate, "yyyy-MM-dd") : null,
          notes: customerNotes || notes || null,
          line_items: lineItems,
          subtotal: totals.totalBilled,
          total_amount: totals.totalBilled,
          qb_sync_status: syncStatus,
          job_type: jobType,
        });
        toast.success("Estimate updated");
      } else {
        await createEstimate.mutateAsync({
          customer_id: customerId || null,
          txn_date: format(txnDate, "yyyy-MM-dd"),
          expiry_date: expiryDate ? format(expiryDate, "yyyy-MM-dd") : null,
          notes: customerNotes || notes || null,
          line_items: lineItems,
          subtotal: totals.totalBilled,
          total_amount: totals.totalBilled,
          qb_sync_status: syncStatus,
          job_type: jobType,
        });
        toast.success(syncStatus === "pending" ? "Saved — QB sync coming soon" : "Estimate saved as draft");
      }
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save estimate");
    }
  };

  const isSaving = createEstimate.isPending || updateEstimate.isPending;

  const canNext1 = !!customerId && !!jobType;
  const hasLines = lines.some((l) => l.description.trim() || l.sub_lines.some((sl) => sl.description.trim()));

  const marginColor = (m: number) => m >= 30 ? "text-emerald-600" : m >= 20 ? "text-yellow-600" : "text-destructive";
  const markupCalcColor = (m: number) => m >= 30 ? "text-emerald-600" : m >= 20 ? "text-yellow-600" : "text-destructive";

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
        <DialogContent className="max-w-[min(950px,95vw)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Edit Estimate" : "New Estimate"} — Step {step} of 2</DialogTitle>
            <DialogDescription>
              {step === 1 ? "Select customer and dates" : "Add SOV line items with labor & material sub-lines"}
            </DialogDescription>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer *</Label>
                  <CustomerSelectWithCreate value={customerId} onValueChange={setCustomerId} />
                </div>
                <div>
                  <Label>Job Type *</Label>
                  <Select value={jobType} onValueChange={(v) => setJobType(v as JobType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flooring">Flooring</SelectItem>
                      <SelectItem value="roofing">Roofing</SelectItem>
                      <SelectItem value="emergency">Emergency Response</SelectItem>
                      <SelectItem value="general">General Construction</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estimate Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(txnDate, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={txnDate} onSelect={(d) => d && setTxnDate(d)} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !expiryDate && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {expiryDate ? format(expiryDate, "PPP") : "Optional"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={expiryDate} onSelect={setExpiryDate} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <Label>Internal Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes..." />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  {/* Load Template */}
                  <Popover open={templatePopoverOpen} onOpenChange={setTemplatePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 text-xs">
                        <FileText className="h-3 w-3 mr-1" /> Load Template
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {templates?.length ? templates.map((tpl) => (
                          <button key={tpl.id} onClick={() => handleLoadTemplate(tpl)}
                            className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors">
                            <div className="font-medium">{tpl.name}</div>
                            <div className="text-xs text-muted-foreground">{(tpl.line_items as TemplateLineItem[]).length} lines · L:{tpl.labor_markup_pct}% M:{tpl.material_markup_pct}%</div>
                          </button>
                        )) : <p className="text-xs text-muted-foreground p-2">No templates yet</p>}
                      </div>
                    </PopoverContent>
                  </Popover>

                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={toggleAllExpanded}>
                    <ChevronsUpDown className="h-3 w-3 mr-1" /> {allExpanded ? "Collapse All" : "Expand All"}
                  </Button>

                  <Button variant="outline" size="sm" className="h-8 text-xs text-destructive hover:text-destructive" onClick={() => setClearConfirmOpen(true)}>
                    <Trash2 className="h-3 w-3 mr-1" /> Clear All
                  </Button>
                </div>

                <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
                  <button
                    className={cn("px-3 py-1.5 transition-colors", viewMode === "internal" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")}
                    onClick={() => setViewMode("internal")}
                  >Internal</button>
                  <button
                    className={cn("px-3 py-1.5 transition-colors border-l border-border", viewMode === "customer" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")}
                    onClick={() => setViewMode("customer")}
                  >Customer</button>
                </div>
              </div>

              {/* INTERNAL VIEW */}
              {viewMode === "internal" && (
                <>
                  {lines.map((line, idx) => {
                    const lt = calcLineTotal(line);
                    const isLS = line.unit === "LS";
                    return (
                      <Collapsible key={idx} open={line.expanded} onOpenChange={() => toggleExpand(idx)}>
                        <div className="border rounded-lg overflow-hidden">
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center gap-1.5 p-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
                              {/* Up/Down arrows */}
                              <div className="flex flex-col gap-0" onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-5 w-5" disabled={idx === 0} onClick={() => moveLine(idx, -1)}>
                                  <ArrowUp className="h-3 w-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-5 w-5" disabled={idx === lines.length - 1} onClick={() => moveLine(idx, 1)}>
                                  <ArrowDown className="h-3 w-3" />
                                </Button>
                              </div>
                              {line.expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                              <span className="text-xs font-mono text-muted-foreground w-6">#{idx + 1}</span>
                              <Input
                                value={line.description}
                                onChange={(e) => { e.stopPropagation(); updateLine(idx, "description", e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Line item description"
                                className="h-8 flex-1"
                              />
                              <Input type="number" value={line.qty}
                                onChange={(e) => { e.stopPropagation(); updateLine(idx, "qty", e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-8 w-20"
                                disabled={isLS}
                              />
                              <Select value={line.unit} onValueChange={(v) => {
                                updateLine(idx, "unit", v);
                                if (v === "LS") updateLine(idx, "qty", "1");
                              }}>
                                <SelectTrigger className="h-8 w-20" onClick={(e) => e.stopPropagation()}><SelectValue /></SelectTrigger>
                                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                              </Select>
                              <span className="font-semibold text-sm w-24 text-right">{fmtCurrency(lt)}</span>
                              <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateLine(idx)} title="Duplicate">
                                  <Copy className="h-3.5 w-3.5" />
                                </Button>
                                {lines.length > 1 && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLine(idx)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <div className="p-3 pt-1 space-y-2 border-t">
                              {line.sub_lines.map((sl, si) => {
                                const cost = parseFloat(sl.cost_price) || 0;
                                const price = parseFloat(sl.unit_price) || 0;
                                const markup = parseFloat(sl.markup_pct) || 0;
                                const slIsLS = sl.unit === "LS";
                                return (
                                  <div key={sl.id} className="space-y-0.5">
                                    <div className="flex items-center gap-1.5 pl-6 flex-wrap">
                                      <Badge variant="outline" className={`text-xs shrink-0 ${sl.type === "labor" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                        {sl.type === "labor" ? "Labor" : "Matl"}
                                      </Badge>
                                      {/* Product/Service picker */}
                                      <Select
                                        value={sl.product_service_id || "custom"}
                                        onValueChange={(v) => { if (v !== "custom") selectProduct(idx, si, v); }}
                                      >
                                        <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="Product..." /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="custom">Custom</SelectItem>
                                          {(() => {
                                            const filtered = allProducts?.filter((p) => {
                                              if (sl.type === "labor") return p.category === "labor";
                                              return p.category === "material" || p.category === "equipment" || p.category === "other";
                                            }) ?? [];
                                            const priorityCodes = JOB_TYPE_PRIORITY[jobType] ?? [];
                                            const prioritySet = new Set(priorityCodes);
                                            const prioritized = filtered.filter(p => prioritySet.has(p.code));
                                            const others = filtered.filter(p => !prioritySet.has(p.code));
                                            const jobLabel = jobType === 'flooring' ? 'Flooring' : jobType === 'roofing' ? 'Roofing' : jobType === 'emergency' ? 'Emergency' : '';
                                            return (
                                              <>
                                                {prioritized.length > 0 && jobLabel && (
                                                  <SelectItem value="__header_priority__" disabled className="text-[10px] font-bold text-muted-foreground">
                                                    ── {jobLabel} ──
                                                  </SelectItem>
                                                )}
                                                {prioritized.map((p) => (
                                                  <SelectItem key={p.id} value={p.id}>
                                                    <span className="font-mono text-[10px] mr-1">{p.code}</span> {p.name}
                                                  </SelectItem>
                                                ))}
                                                {others.length > 0 && prioritized.length > 0 && (
                                                  <SelectItem value="__header_other__" disabled className="text-[10px] font-bold text-muted-foreground">
                                                    ── Other ──
                                                  </SelectItem>
                                                )}
                                                {others.map((p) => (
                                                  <SelectItem key={p.id} value={p.id}>
                                                    <span className="font-mono text-[10px] mr-1">{p.code}</span> {p.name}
                                                  </SelectItem>
                                                ))}
                                              </>
                                            );
                                          })()}
                                        </SelectContent>
                                      </Select>
                                      <Input value={sl.description} onChange={(e) => updateSubLine(idx, si, "description", e.target.value)}
                                        placeholder={sl.type === "labor" ? "Labor desc" : "Material desc"} className="h-8 flex-1 min-w-0" />
                                      {(() => {
                                        const slProduct = sl.product_service_id ? allProducts?.find(p => p.id === sl.product_service_id) : null;
                                        const hasMapping = slProduct?.qty_mapping?.auto_apply;
                                        const isOverridden = overriddenMappings.current.has(sl.id);
                                        return (
                                          <div className="relative flex items-center">
                                            <Input type="number" value={sl.qty} onChange={(e) => updateSubLine(idx, si, "qty", e.target.value)}
                                              className={cn("h-8 w-14", hasMapping && !isOverridden && "pr-5")} disabled={slIsLS}
                                              title={hasMapping ? (isOverridden ? `Overridden — was: ${slProduct?.qty_mapping?.formula}` : `Auto: ${slProduct?.qty_mapping?.formula}`) : undefined} />
                                            {hasMapping && (
                                              <span className="absolute right-1 text-[10px]" title={slProduct?.qty_mapping?.note || slProduct?.qty_mapping?.formula}>
                                                {isOverridden ? "✏️" : "🔗"}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      <Select value={sl.unit} onValueChange={(v) => updateSubLine(idx, si, "unit", v)}>
                                        <SelectTrigger className="h-8 w-14"><SelectValue /></SelectTrigger>
                                        <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                                      </Select>
                                      {(() => {
                                        const slProduct = sl.product_service_id ? allProducts?.find(p => p.id === sl.product_service_id) : null;
                                        const hasCostMapping = slProduct?.qty_mapping?.use_as === 'cost_price';
                                        return (
                                          <div className="flex items-center gap-0.5">
                                            <span className="text-[10px] text-muted-foreground">Cost</span>
                                            <div className="relative">
                                              <Input type="number" value={sl.cost_price} onChange={(e) => updateSubLine(idx, si, "cost_price", e.target.value)} className="h-8 w-[70px]" step="0.01"
                                                title={hasCostMapping ? `Auto: ${slProduct?.qty_mapping?.formula}` : undefined} />
                                              {hasCostMapping && <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px]" title={slProduct?.qty_mapping?.note}>🔗</span>}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      <div className="flex items-center gap-0.5">
                                        <span className="text-[10px] text-muted-foreground">%</span>
                                        <Input type="number" value={sl.markup_pct} onChange={(e) => updateSubLine(idx, si, "markup_pct", e.target.value)} className="h-8 w-20 min-w-[80px]" />
                                      </div>
                                      <div className="flex items-center gap-0.5">
                                        <span className="text-[10px] text-muted-foreground">{slIsLS ? "Total" : "@"}</span>
                                        <Input type="number" value={sl.unit_price} onChange={(e) => updateSubLine(idx, si, "unit_price", e.target.value)} className="h-8 w-[70px]" step="0.01" />
                                      </div>
                                      {sl.type === "material" && (
                                        <div className="flex items-center gap-1 shrink-0" title="Taxable (QB)">
                                          <Switch checked={sl.taxable} onCheckedChange={(v) => updateSubLine(idx, si, "taxable", v)} className="scale-75" />
                                          <span className="text-[10px] text-muted-foreground">Tax</span>
                                        </div>
                                      )}
                                      <span className="text-sm font-medium w-20 text-right shrink-0">{fmtCurrency(subLineBilled(sl))}</span>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeSubLine(idx, si)}>
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    {cost > 0 && (
                                      <p className={cn("text-[11px] pl-6 ml-[52px]", markupCalcColor(markup))}>
                                        Cost {fmtCurrency(cost)} + {markup.toFixed(0)}% = {fmtCurrency(price)} billed
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                              <div className="flex gap-2 pl-6 pt-1">
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addSubLine(idx, "labor")}>
                                  <Plus className="h-3 w-3 mr-1" /> Labor
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addSubLine(idx, "material")}>
                                  <Plus className="h-3 w-3 mr-1" /> Material
                                </Button>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}

                  {/* Tax note — once at bottom */}
                  <p className="text-xs text-muted-foreground italic">
                    Tax rate applied at QB sync per company tax settings.
                  </p>
                </>
              )}

              {/* CUSTOMER VIEW */}
              {viewMode === "customer" && (
                <div className="space-y-2">
                  {lines.map((line, idx) => {
                    const combinedUnitPrice = line.sub_lines.reduce((s, sl) => s + (parseFloat(sl.unit_price) || 0), 0);
                    const qty = parseFloat(line.qty) || 0;
                    const lineTotal = qty * combinedUnitPrice;
                    return (
                      <div key={idx} className="flex items-center gap-2 p-3 border rounded-lg">
                        <span className="text-xs font-mono text-muted-foreground w-6">#{idx + 1}</span>
                        <div className="flex-1 flex items-center gap-1">
                          <Input
                            value={line.customer_description || line.description}
                            onChange={(e) => updateLine(idx, "customer_description", e.target.value)}
                            placeholder="Customer-facing description"
                            className="h-8 flex-1"
                          />
                          <Pencil className="h-3 w-3 text-muted-foreground shrink-0" />
                        </div>
                        <span className="text-sm text-muted-foreground w-16 text-right">{qty} {line.unit}</span>
                        <span className="text-sm text-muted-foreground w-24 text-right">@ {fmtCurrency(combinedUnitPrice)}</span>
                        <span className="font-semibold text-sm w-24 text-right">{fmtCurrency(lineTotal)}</span>
                        {lines.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLine(idx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <Button variant="outline" size="sm" onClick={() => setLines((p) => [...p, emptyLine()])}>
                <Plus className="h-4 w-4 mr-1" /> Add Line Item
              </Button>

              {/* Master Markup + Profit Summary */}
              <div className={cn("grid gap-4", viewMode === "internal" ? "grid-cols-2" : "grid-cols-1")}>
                {viewMode === "internal" && (
                  <div className="flex flex-col gap-2 p-3 border rounded-lg bg-muted/20">
                    <Label className="text-xs font-semibold">Markup Controls</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14">Labor</span>
                      <Input type="number" value={laborMarkup} onChange={(e) => setLaborMarkup(e.target.value)} className="h-8 w-20 min-w-[80px]" />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14">Material</span>
                      <Input type="number" value={materialMarkup} onChange={(e) => setMaterialMarkup(e.target.value)} className="h-8 w-20 min-w-[80px]" />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                    <Button size="sm" variant="outline" className="h-8 text-xs self-start" onClick={applyMasterMarkup}>Apply to All</Button>
                  </div>
                )}

                {viewMode === "internal" && (
                  <div className="border rounded-lg p-3 space-y-1 bg-yellow-50/50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center gap-1 text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                      <AlertTriangle className="h-3 w-3" /> INTERNAL — Not shown to customer
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-muted-foreground text-xs">
                          <th className="text-left font-normal pb-1"></th>
                          <th className="text-right font-normal pb-1">Cost</th>
                          <th className="text-right font-normal pb-1">Billed</th>
                          <th className="text-right font-normal pb-1">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-muted-foreground">Labor</td>
                          <td className="text-right">{fmtCurrency(totals.laborCost)}</td>
                          <td className="text-right">{fmtCurrency(totals.laborBilled)}</td>
                          <td className={cn("text-right", marginColor(totals.laborMargin))}>{totals.laborMargin.toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td className="text-muted-foreground">Material</td>
                          <td className="text-right">{fmtCurrency(totals.materialCost)}</td>
                          <td className="text-right">{fmtCurrency(totals.materialBilled)}</td>
                          <td className={cn("text-right", marginColor(totals.materialMargin))}>{totals.materialMargin.toFixed(1)}%</td>
                        </tr>
                        <tr className="font-semibold border-t">
                          <td>Total</td>
                          <td className="text-right">{fmtCurrency(totals.totalCost)}</td>
                          <td className="text-right">{fmtCurrency(totals.totalBilled)}</td>
                          <td className={cn("text-right", marginColor(totals.margin))}>{totals.margin.toFixed(1)}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer Subtotals */}
              <div className="border rounded-lg p-4 space-y-1 bg-muted/30">
                <div className="flex justify-between text-sm">
                  <span>Labor Subtotal (billed)</span><span>{fmtCurrency(totals.laborBilled)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Material Subtotal (billed)</span><span>{fmtCurrency(totals.materialBilled)}</span>
                </div>
                <div className="flex justify-between font-semibold text-base border-t pt-2 mt-2">
                  <span>Total Amount</span><span>{fmtCurrency(totals.totalBilled)}</span>
                </div>
              </div>

              <div>
                <Label>Customer Notes (visible to customer — maps to QB CustomerMemo)</Label>
                <Textarea
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder="Notes visible to customer on the estimate..."
                  className="min-h-[80px]"
                />
              </div>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <div>
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {step === 1 ? (
                <Button onClick={() => setStep(2)} disabled={!canNext1}>
                  Next <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => handleSave("not_synced")} disabled={!hasLines || isSaving}>
                    {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {isEditMode ? "Update Draft" : "Save as Draft"}
                  </Button>
                  <Button onClick={() => handleSave("pending")} disabled={!hasLines || isSaving}>
                    {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {isEditMode ? "Update & Sync to QB" : "Save & Send to QB"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template load mode dialog */}
      <AlertDialog open={!!pendingTemplate} onOpenChange={(v) => { if (!v) setPendingTemplate(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Load Template: {pendingTemplate?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              You already have line items. How would you like to load the template?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2 sm:justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyTemplate("append")} className="bg-secondary text-secondary-foreground hover:bg-secondary/80">
              Append to existing lines
            </AlertDialogAction>
            <AlertDialogAction onClick={() => applyTemplate("replace")}>
              Replace all lines
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear all confirmation */}
      <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all line items?</AlertDialogTitle>
            <AlertDialogDescription>This will remove all line items and sub-lines. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setLines([emptyLine()]); setClearConfirmOpen(false); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
